import * as React from 'react';
import { createPortal } from "react-dom";
import { TextArea, TextAreaChangeEvent } from "@progress/kendo-react-inputs";
import { State } from '@progress/kendo-data-query';
import { useODataContext } from "contexts/ODataContext";
import "../../../styles/custom/css/mention.css";

export const MentionInput = ({ value, currentUserId, onChange, placeholder, onSend, maxLength }: any) => {
  const { data, loadData } = useODataContext();
  const [filteredUsers, setFilteredUsers] = React.useState<any[]>([]);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [cursorPosition, setCursorPosition] = React.useState(0);
  const [currentIds, setCurrentIds] = React.useState<number[]>([]);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [coords, setCoords] = React.useState({ top: "0px", left: "0px", width: "0px" });
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const dropdownRef = React.useRef<HTMLUListElement>(null);

  // Reset index when filteredUsers change
  React.useEffect(() => {
    setSelectedIndex(0);
  }, [filteredUsers]);

  React.useEffect(() => {
    const initialState: State = { skip: 0, take: 10000 };
    // Load all users initially
    loadData(initialState);
  }, []);

  React.useEffect(() => {
    if (showDropdown && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const menuHeight = Math.min(filteredUsers.length * 40, 200);
      
      setCoords({
        top: `${rect.top - menuHeight - 10}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`
      });
    }
  }, [showDropdown, filteredUsers.length]);

  React.useEffect(() => {
    if (showDropdown && dropdownRef.current) {
      const selectedElement = dropdownRef.current.childNodes[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: "nearest", // scroll if necessary
          behavior: "smooth" 
        });
      }
    }
  }, [selectedIndex, showDropdown]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showDropdown) {
      if (e.nativeEvent.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredUsers.length);
      } else if (e.nativeEvent.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filteredUsers.length) % filteredUsers.length);
      } else if (e.nativeEvent.key === "Enter") {
        e.preventDefault();
        insertMention(filteredUsers[selectedIndex]);
      } else if (e.nativeEvent.key === "Escape") {
        setShowDropdown(false);
      }
    } else {
      if (e.nativeEvent.key === "Enter" && !e.nativeEvent.shiftKey) {
        e.preventDefault();
        onSend();
      }
    }
  };

  const handleInputChange = (e: TextAreaChangeEvent) => {
    const val = e.value;
    const caret = e.nativeEvent?.target?.selectionStart || 0;
    const pre = val.slice(0, caret);
    const match = pre.match(/@(\w*)$/);

    if (match) {
      const query = match[1].toLowerCase();
      
      // Mapping OData fields (UserId/Username) to the mention format
      const mapped = (data || []).map((u: any) => ({
        id: u.UserId,
        name: u.Username || "" 
      }));

      const filtered = mapped.filter((u: any) => 
        u.name && u.name.toLowerCase().includes(query) && u.id !== currentUserId && !currentIds.includes(u.id)
      );
      setFilteredUsers(filtered);
      setShowDropdown(filtered.length > 0);
    } else {
      setShowDropdown(false);
    }

    setCursorPosition(caret);
    syncChanges(val);
  };

  const insertMention = (user: { id: number; name: string }) => {
    const textBeforeCursor = value.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
  
    if (lastAtIndex !== -1) {
      const part1 = value.substring(0, lastAtIndex);
      const part2 = value.substring(cursorPosition);
      
      const mention = `@${user.name}`;
      const updatedValue = `${part1}${mention} ${part2}`;
      const newCursorPos = part1.length + mention.length;
  
      syncChanges(updatedValue);
      setShowDropdown(false);
  
      // Search for the textarea in the DOM starting from the container
      setTimeout(() => {
        const nativeTextArea = containerRef.current?.querySelector('textarea');
        if (nativeTextArea) {
          nativeTextArea.focus();
          nativeTextArea.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }
  };

  const syncChanges = (nextText: string) => {
    const ids = (data || [])
      .filter(u => nextText.includes(`@${u.Username}`))
      .map(u => u.UserId);
    setCurrentIds(ids);
    onChange(nextText, ids); 
  };

  return (
    <div ref={containerRef} className="mention-input-container">
      <TextArea
        autoSize={true}
        rows={1}
        value={value}
        className="input-message-area"
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
      />

      {showDropdown && createPortal(
        <ul 
          ref={dropdownRef} 
          className="mention-dropdown-menu"
          style={{ 
            "--menu-top": coords.top, 
            "--menu-left": coords.left, 
            "--menu-width": coords.width 
          } as React.CSSProperties}
        >
          {filteredUsers.map((user, index) => (
            <li
              key={user.id}
              className={`mention-item ${index === selectedIndex ? "selected" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                insertMention(user);
              }}
            >
              @{user.name}
            </li>
          ))}
        </ul>,
        document.body
      )}
    </div>
  );
};